import React from 'react';
import { useStatus } from '../contexts/StatusContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiInfo, FiServer, FiCloud, FiGlobe } = FiIcons;

const AdvancedSettings = () => {
  const { settings, updateSettings } = useStatus();

  const handleChange = (key, value) => {
    updateSettings({ [key]: value });
  };

  return (
    <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-4">
      <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-4">
        Advanced Options
      </h3>
      
      <div className="space-y-4">
        {/* Check Method Selection */}
        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3 flex items-center space-x-2">
            <SafeIcon icon={FiServer} className="h-4 w-4" />
            <span>Check Method</span>
          </h4>
          <div className="space-y-2">
            <label className="flex items-start space-x-2">
              <input
                type="radio"
                name="checkMethod"
                checked={settings.useServer}
                onChange={(e) => handleChange('useServer', e.target.checked)}
                className="mt-0.5 rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500"
              />
              <div>
                <span className="text-sm text-gray-700 dark:text-gray-300 font-medium">
                  Server-Side Check (Recommended)
                </span>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Complete analysis including headers, security info, and performance metrics
                </p>
              </div>
            </label>
            <label className="flex items-start space-x-2">
              <input
                type="radio"
                name="checkMethod"
                checked={!settings.useServer}
                onChange={(e) => handleChange('useServer', !e.target.checked)}
                className="mt-0.5 rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500"
              />
              <div>
                <span className="text-sm text-gray-700 dark:text-gray-300 font-medium">
                  Proxy/Client-Side Check
                </span>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Uses Cloudflare Worker proxy or browser-based checks as fallback
                </p>
              </div>
            </label>
          </div>
        </div>

        {/* Basic Settings Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
              Protocol
            </label>
            <select
              value={settings.protocol}
              onChange={(e) => handleChange('protocol', e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus-ring"
            >
              <option value="https">HTTPS</option>
              <option value="http">HTTP</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
              Timeout (ms)
            </label>
            <input
              type="number"
              min="1000"
              max="30000"
              step="1000"
              value={settings.timeoutMs}
              onChange={(e) => handleChange('timeoutMs', parseInt(e.target.value))}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus-ring"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
              Max Redirects
            </label>
            <input
              type="number"
              min="0"
              max="10"
              value={settings.maxRedirects}
              onChange={(e) => handleChange('maxRedirects', parseInt(e.target.value))}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus-ring"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
              Response Time Threshold (ms)
            </label>
            <input
              type="number"
              min="500"
              max="10000"
              step="500"
              value={settings.thresholdMs}
              onChange={(e) => handleChange('thresholdMs', parseInt(e.target.value))}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus-ring"
            />
          </div>
        </div>

        {/* DNS Settings */}
        <div>
          <label className="flex items-start space-x-2 mb-2">
            <input
              type="checkbox"
              checked={settings.enableDNS}
              onChange={(e) => handleChange('enableDNS', e.target.checked)}
              className="mt-0.5 rounded border-gray-300 dark:border-gray-600 text-primary-600 focus:ring-primary-500"
            />
            <span className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">
              Enable DNS lookup via DoH (Cloudflare) for comprehensive analysis
            </span>
          </label>
        </div>

        {/* Information Box */}
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded p-3">
          <div className="flex items-start space-x-2">
            <SafeIcon icon={FiInfo} className="h-4 w-4 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-xs text-blue-800 dark:text-blue-400 leading-relaxed">
                <strong>Check Priority:</strong> Server-side → Cloudflare Worker → Direct Browser → Limited Fallback
              </p>
              <div className="mt-2 space-y-1 text-xs text-blue-700 dark:text-blue-300">
                <div className="flex items-center space-x-2">
                  <SafeIcon icon={FiServer} className="h-3 w-3" />
                  <span><strong>Server:</strong> Complete analysis with all headers and security info</span>
                </div>
                <div className="flex items-center space-x-2">
                  <SafeIcon icon={FiCloud} className="h-3 w-3" />
                  <span><strong>Proxy:</strong> CORS-bypass with basic response data</span>
                </div>
                <div className="flex items-center space-x-2">
                  <SafeIcon icon={FiGlobe} className="h-3 w-3" />
                  <span><strong>Limited:</strong> DNS + favicon test for blocked sites</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdvancedSettings;